import { useCallback, useEffect, useState } from "react";
import {checkPermissionsAndRedirect, hasPermission} from "../../../../configs/functions";
import { listaEmpresas } from "../../management/companies/configs/functions";
import { getDashboard } from "./configs/functions";
import { COLORS, METRICS } from "./configs/config";
import { Box, Breadcrumbs, Grid, Typography } from "@mui/material";
import { DashboardFilters } from "./components/DashboardFilters";
import { EpiCard } from "./components/DashboardCards";
import { ConsumoChart } from "./components/charts/ConsumoChart";
import { RetiradasChart } from "./components/charts/RetiradasChart";
import { CostCenterChart } from "./components/charts/CostCenterChart";
import { AlertsChart } from "./components/charts/AlertsChart";
import { ProductRankingCard } from "./components/charts/ProductRankingCard";
import { AreasChart } from "./components/charts/AreasChart";
import { useCommonItems } from "../../../../contexts/CommonItensProvider";
import LinkNavegacao from "../../../common/Link";
import { Assessment, Home as HomeIcon } from "@mui/icons-material";
import PageTitle from "../../../common/PageTitle";

export default function EpiDashboard() {
    const { exibirAlerta } = useCommonItems();

    const [empresas, setEmpresas] = useState([]);
    const [empresa, setEmpresa]  = useState(null);

    const [period, setPeriod]   = useState(30);
    const [year, setYear]     = useState(new Date().getFullYear());

    const [data, setData]     = useState(null);

    const [loading, setLoading]  = useState(false);
    const [error, setError]    = useState(null);

    useEffect(() => {
        if (hasPermission(["admin", "admin_empresas", "list_empresas"])) {
            listaEmpresas(null, null, null, true)
                .then((response) => {
                    const raw = response.data.data;
                    const list = Array.isArray(raw) ? raw : [raw];
                    setEmpresas(list);
                    if (list.length > 0) setEmpresa(list[0].id);
                })
                .catch((e) => setError(e.message));
        }
    }, []);

    async function fetchDashboard({ companies, period, years, metrics, isFilter }) {
        const res = await getDashboard({ companies, period, years, metrics, isFilter });
        return res.data;
    }

    const load = useCallback(() => {
        if (!empresa) return;
        setLoading(true);
        setError(null);
        fetchDashboard({
            companies: [empresa],
            period,
            years: [year, year - 1],
            metrics: METRICS,
            isFilter: true
        })
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [empresa, period, year]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "dashboard"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (error) exibirAlerta("Erro", "Ocorreu um erro ao carregar informações", "error");
    }, [error]);

    const epis = [
        {
            label: "Retiradas — período",
            valor: (data?.retiradasRequisicoes?.totalRetiradas   ?? 0).toLocaleString("pt-BR"),
            badge: `${period}d`,
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "🏃",
        },
        {
            label: "Requisições — período",
            valor: (data?.retiradasRequisicoes?.totalRequisicoes ?? 0).toLocaleString("pt-BR"),
            badge: `${period}d`,
            badgeColor: COLORS.g2,
            badgeBg: COLORS.kpiBg,
            icon: "📋",
        },
        {
            label: "Funcionários c/ EPI vencido",
            valor: (data?.totalEpisVencidos?.quantidade ?? 0).toLocaleString("pt-BR"),
            badge: "urgente",
            badgeColor: COLORS.amber,
            badgeBg: COLORS.kpiBgWarn,
            icon: "⚠️",
            valorColor: COLORS.amber,
        },
        {
            label: "CAs vencidos",
            valor: (data?.CAVencidos?.quantidade ?? 0).toLocaleString("pt-BR"),
            badge: "crítico",
            badgeColor: COLORS.red,
            badgeBg: COLORS.kpiBgDanger,
            icon: "❌",
            valorColor: COLORS.red,
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh" }}>
            <Grid item xs={12} mb={5}>
                <Breadcrumbs>
                    <LinkNavegacao to="/"><HomeIcon fontSize="small" /></LinkNavegacao>
                    <LinkNavegacao to="/epis">Gestão de EPI's</LinkNavegacao>
                    <Typography variant="span">Dashboard</Typography>
                </Breadcrumbs>
                <PageTitle icon={<Assessment fontSize="large" />} title="Dashboard" />
            </Grid>

            <DashboardFilters
                empresas={empresas}
                empresa={empresa}
                setEmpresa={setEmpresa}
                period={period}
                setPeriod={setPeriod}
                year={year}
                setYear={setYear}
                loading={loading}
            />

            <Grid container spacing={1.5} mb={1.5}>
                {epis.map((k, i) => (
                    <Grid key={i} item xs={12} sm={6} md={3}>
                        <EpiCard {...k} />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={1.5} mb={1.5}>
                <Grid item xs={12} md={5}>
                    <ConsumoChart data={data?.consumoEpiFunc} loading={loading} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RetiradasChart data={data?.retiradasRequisicoes} period={period} loading={loading} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <CostCenterChart data={data?.consumoEPI} loading={loading} />
                </Grid>
            </Grid>

            <Grid container spacing={1.5}>
                <Grid item xs={12} md={3}>
                    <AlertsChart data={data} loading={loading} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ProductRankingCard data={data?.rankingProdutos} loading={loading} />
                </Grid>
                <Grid item xs={12} md={5}>
                    <AreasChart data={data?.requisicoesArea} loading={loading} />
                </Grid>
            </Grid>
        </Box>
    );
}